
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
    ChevronRight, 
    Shield, 
    Zap, 
    Lock, 
    Fingerprint, 
    Globe, 
    Layers, 
    CheckCircle2, 
    Infinity,
    Smartphone,
    CreditCard,
    ArrowRight,
    Building2,
    BarChart3,
    Cpu,
    Bot,
    GanttChartSquare,
    Link2
} from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from '../ui/Button';
import { CreditCardMockup } from '../marketing/CreditCardMockup';

interface GhostCardLandingProps {
    onLogin: () => void;
    onNavigate: (page: 'landing' | 'docs' | 'solutions' | 'card-landing' | 'about-landing' | 'legal') => void;
    onNavigateToDocs: (section?: string) => void;
}

const FeatureCard = ({ icon: Icon, title, desc }: { icon: any, title: string, desc: string }) => (
    <div className="p-10 border border-stone-100 dark:border-white/5 bg-white dark:bg-[#15171C] rounded-2xl hover:border-nobel-gold/50 transition-all duration-500 group shadow-sm hover:shadow-2xl">
        <div className="w-14 h-14 bg-stone-900 dark:bg-white rounded-2xl flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] mb-8 group-hover:scale-110 transition-transform duration-500 shadow-lg">
            <Icon size={28} />
        </div>
        <h3 className="text-2xl font-bold mb-4 font-serif text-[#0A0B0F] dark:text-white tracking-tight">{title}</h3>
        <p className="text-stone-500 dark:text-stone-400 text-sm leading-relaxed font-medium">{desc}</p>
    </div>
);

const EnterpriseBenefit = ({ icon: Icon, title, desc }: { icon: any, title: string, desc: string }) => (
    <div className="flex flex-col sm:flex-row gap-6 p-8 rounded-3xl bg-white dark:bg-[#0F1014] border border-stone-100 dark:border-white/5 transition-all hover:bg-stone-50 dark:hover:bg-white/[0.02] shadow-sm hover:shadow-xl group">
        <div className="w-16 h-16 shrink-0 rounded-2xl bg-stone-900 dark:bg-white flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] shadow-2xl group-hover:scale-105 transition-transform duration-500">
            <Icon size={32} />
        </div>
        <div>
            <h4 className="font-serif text-2xl font-bold text-[#0A0B0F] dark:text-white mb-3 tracking-tight">{title}</h4>
            <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed font-medium">{desc}</p>
        </div>
    </div>
);

export const GhostCardLanding: React.FC<GhostCardLandingProps> = ({ onLogin, onNavigate, onNavigateToDocs }) => {
    return (
        <div className="bg-white dark:bg-[#0A0B0F] min-h-screen text-[#0A0B0F] dark:text-white selection:bg-nobel-gold selection:text-black">
            <Header onLogin={onLogin} onNavigateToDocs={onNavigateToDocs} onNavigate={(p) => onNavigate(p)} />

            <main>
                {/* 1. Elite Hero Section - Responsive Flex */}
                <section className="relative pt-24 pb-32 md:pt-40 md:pb-48 overflow-hidden bg-white dark:bg-[#0A0B0F]">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-nobel-gold/10 via-transparent to-transparent opacity-60"></div>
                    
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
                            <div className="w-full lg:w-1/2 order-2 lg:order-1 text-center lg:text-left">
                                <motion.div
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.8 }}
                                >
                                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-[10px] font-black uppercase tracking-[0.4em] text-stone-500 mb-10 border border-stone-200 dark:border-white/10 shadow-sm">
                                        Aerospace Titanium Edition
                                    </div>
                                    <h1 className="font-serif text-5xl md:text-8xl font-bold leading-[0.95] mb-10 tracking-tighter text-[#0A0B0F] dark:text-white">
                                        Ghost <br />
                                        <span className="italic text-stone-400 dark:text-stone-500 font-light text-6xl md:text-9xl">Credit Card.</span>
                                    </h1>
                                    <p className="text-lg md:text-xl text-stone-600 dark:text-stone-400 max-w-lg mx-auto lg:mx-0 mb-12 leading-relaxed font-light">
                                        The physical manifestation of on-chain sovereignty. Spend SOL, SUI, and USDC anywhere globally with sub-second titanium-level finality.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start px-4 sm:px-0">
                                        <Button onClick={onLogin} size="lg" variant="secondary" className="h-16 px-10 text-base shadow-[0_30px_60px_-12px_rgba(0,0,0,0.3)] bg-[#0A0B0F] text-white dark:bg-white dark:text-black rounded-sm font-black uppercase tracking-widest group">
                                            Issue Virtual Card <ChevronRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                        </Button>
                                        <Button onClick={onLogin} size="lg" variant="outline" className="h-16 px-10 text-base bg-white dark:bg-transparent border-stone-200 dark:border-white/10 text-stone-800 dark:text-white rounded-sm font-black uppercase tracking-widest hover:bg-stone-50 dark:hover:bg-white/5 transition-all">
                                            Request Invite
                                        </Button>
                                    </div>
                                </motion.div>
                            </div>
                            <div className="w-full lg:w-1/2 order-1 lg:order-2 flex justify-center">
                                <div className="w-full max-w-2xl relative scale-90 sm:scale-100">
                                    <div className="absolute inset-0 bg-nobel-gold/10 blur-[120px] rounded-full animate-pulse"></div>
                                    <CreditCardMockup />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 2. Enterprise Benefits Breakdown - Responsive Stack */}
                <section className="py-32 md:py-48 bg-stone-50 dark:bg-[#0D0E12] border-y border-stone-200 dark:border-white/5">
                    <div className="container mx-auto px-6">
                        <div className="flex flex-col lg:flex-row gap-20 lg:gap-24 items-start">
                            <div className="w-full lg:w-1/3 lg:sticky lg:top-32 text-center lg:text-left">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A0B0F] dark:bg-white text-white dark:text-black text-[10px] font-black uppercase tracking-[0.4em] mb-8 shadow-xl">
                                    Enterprise Rails
                                </div>
                                <h2 className="font-serif text-4xl md:text-6xl font-bold text-[#0A0B0F] dark:text-white mb-8 tracking-tighter leading-tight">Built for <br/><span className="italic text-stone-400">Institutional</span> Control.</h2>
                                <p className="text-lg text-stone-500 dark:text-stone-400 leading-relaxed font-light mb-12 max-w-md mx-auto lg:mx-0">
                                    Provision your corporate treasury with granular programmatic logic. Issue thousands of cards for staff, departments, and autonomous AI agents.
                                </p>
                                <Button variant="outline" onClick={onLogin} className="rounded-sm h-16 px-10 bg-white !text-black border-transparent shadow-2xl group font-black uppercase tracking-widest">
                                    Enterprise Specs <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </div>
                            
                            <div className="w-full lg:w-2/3 grid grid-cols-1 md:grid-cols-1 gap-6">
                                <EnterpriseBenefit 
                                    icon={Building2}
                                    title="Treasury Integration"
                                    desc="Connect your Ghost Card program directly to your corporate SUI or Solana vault for real-time, high-liquidity pooling and automated sweep protocols."
                                />
                                <EnterpriseBenefit 
                                    icon={GanttChartSquare}
                                    title="Programmable Controls"
                                    desc="Define spending rules per card based on real-world time, specific merchant categories, or dynamic on-chain block-height thresholds."
                                />
                                <EnterpriseBenefit 
                                    icon={Bot}
                                    title="Autonomous AI Agents"
                                    desc="Provision specialized cards for AI agents using the x402 protocol, enabling autonomous API, data, and infrastructure payments with hard budget caps."
                                />
                                <EnterpriseBenefit 
                                    icon={Link2}
                                    title="Real-time ERP Webhooks"
                                    desc="Sync every transaction signature instantly with your existing accounting software via our enterprise-grade websocket relays and audit streams."
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3. Physical Materiality Section - Vertical Stack on mobile */}
                <section className="py-32 md:py-48 bg-[#050505] text-white relative overflow-hidden">
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-center">
                            <div className="space-y-10 text-center lg:text-left">
                                <h2 className="font-serif text-5xl md:text-7xl font-bold tracking-tighter leading-tight">Precision <br /><span className="text-stone-700 italic">Materiality.</span></h2>
                                <p className="text-xl text-stone-400 leading-relaxed font-light max-w-lg mx-auto lg:mx-0">
                                    Machined from a solid block of aerospace-grade titanium. Laser-etched with your unique Ghost ID. Forged for those who demand the weight of sovereignty.
                                </p>
                                <div className="grid grid-cols-2 gap-10 md:gap-16 pt-12 border-t border-white/10">
                                    <div className="group">
                                        <div className="text-5xl md:text-6xl font-serif font-bold text-white mb-2 group-hover:text-nobel-gold transition-colors tracking-tighter">32g</div>
                                        <div className="text-[10px] uppercase font-black tracking-[0.4em] text-stone-600">SOLID TITANIUM</div>
                                    </div>
                                    <div className="group">
                                        <div className="text-5xl md:text-6xl font-serif font-bold text-white mb-2 group-hover:text-nobel-gold transition-colors tracking-tighter">0.76mm</div>
                                        <div className="text-[10px] uppercase font-black tracking-[0.4em] text-stone-600">AEROSPACE THINNESS</div>
                                    </div>
                                </div>
                            </div>
                            <div className="relative aspect-square flex items-center justify-center scale-90 sm:scale-100">
                                <div className="absolute inset-0 bg-nobel-gold/5 rounded-full blur-[140px] animate-pulse"></div>
                                <motion.div 
                                    initial={{ opacity: 0, rotate: -15, scale: 0.8 }}
                                    whileInView={{ opacity: 1, rotate: 0, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 1.2, ease: "easeOut" }}
                                    className="relative z-10 w-full"
                                >
                                    <div className="relative w-full aspect-[1.586/1] bg-[#0A0B0F] rounded-[24px] border border-white/10 p-10 md:p-14 overflow-hidden shadow-[0_60px_120px_-20px_rgba(0,0,0,1)] group">
                                         <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#050505] to-[#121212]"></div>
                                         <div className="absolute inset-0 opacity-[0.08] bg-[url('https://www.transparenttextures.com/patterns/brushed-alum.png')]"></div>
                                         <div className="relative z-10 h-full flex flex-col justify-between">
                                            <div className="flex justify-between items-start">
                                                <div className="w-16 h-11 rounded-sm bg-gradient-to-br from-nobel-gold to-[#8A6D3B] border border-black/30 shadow-2xl" />
                                                <Smartphone className="text-white/20 group-hover:text-nobel-gold transition-colors duration-700" size={40} />
                                            </div>
                                            <div>
                                                <div className="text-2xl md:text-3xl font-mono text-white/90 tracking-[0.3em] drop-shadow-2xl">•••• •••• •••• 9201</div>
                                                <div className="flex justify-between items-end mt-10 pt-10 border-t border-white/5">
                                                    <div className="text-[10px] font-black tracking-[0.4em] text-white/30 uppercase">Ghost Network Protocol</div>
                                                    <div className="font-serif italic font-bold text-3xl text-white tracking-tighter">Titanium</div>
                                                </div>
                                            </div>
                                         </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 4. Feature Grid - Responsive Single Column on Mobile */}
                <section className="py-32 md:py-48 bg-white dark:bg-[#0A0B0F]">
                    <div className="container mx-auto px-6">
                        <div className="text-center max-w-3xl mx-auto mb-24 space-y-6">
                            <h2 className="font-serif text-4xl md:text-6xl font-bold tracking-tighter text-[#0A0B0F] dark:text-white leading-tight">The Decentralized Core.</h2>
                            <p className="text-lg md:text-xl text-stone-500 font-light leading-relaxed">Instant issuance. Native on-chain settlement. Sovereign control over every transaction.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <FeatureCard 
                                icon={Globe} 
                                title="Multi-Chain Native" 
                                desc="Directly spend assets from Solana, Sui, and USDC. GhostOS handles automatic routing and swapping at the tap of your card." 
                            />
                            <FeatureCard 
                                icon={Layers} 
                                title="Deterministic Routing" 
                                desc="Our engine executes payments via the fastest and most cost-efficient rails available, ensuring sub-second retail performance." 
                            />
                            <FeatureCard 
                                icon={Shield} 
                                title="Stealth Protocol" 
                                desc="Enable privacy mode to route all physical card spend through one-time stealth addresses, isolating your physical life from your vault history." 
                            />
                            <FeatureCard 
                                icon={Fingerprint} 
                                title="Biometric Security" 
                                desc="Authorize high-value institutional payments directly from your hardware secure enclave using FaceID or hardware keys." 
                            />
                            <FeatureCard 
                                icon={Smartphone} 
                                title="Virtual Provisioning" 
                                desc="Instantly generate virtual cards with unique identifiers and specific limits directly from the GhostWallet mobile OS." 
                            />
                            <FeatureCard 
                                icon={Zap} 
                                title="Atomic Velocity" 
                                desc="Experience the speed of legacy fiat settlement combined with the transparency of the blockchain. sub-400ms authorization." 
                            />
                        </div>
                    </div>
                </section>

                {/* 5. CTA Footer Section */}
                <section className="py-40 md:py-56 bg-[#0A0B0F] relative overflow-hidden text-center border-t border-white/5 px-6">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                    <div className="container mx-auto relative z-10">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <h2 className="font-serif text-6xl md:text-9xl font-bold text-white mb-12 tracking-tighter leading-none">
                                Own the <br/><span className="text-stone-700 italic">Rails.</span>
                            </h2>
                            <p className="text-lg md:text-2xl text-stone-500 mb-16 max-w-2xl mx-auto font-light leading-relaxed">
                                Issue your virtual Ghost Card in seconds. Reclaim your financial privacy. No banks. No borders. Pure code.
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-6">
                                <Button onClick={onLogin} size="lg" variant="secondary" className="bg-white !text-black h-18 px-14 text-lg shadow-2xl shadow-white/5 hover:bg-stone-200 font-black uppercase tracking-widest rounded-sm">
                                    Initialize Card
                                </Button>
                                <Button onClick={onLogin} size="lg" variant="outline" className="h-18 px-14 text-lg border-white/20 text-white hover:bg-white/10 group font-black uppercase tracking-widest rounded-sm">
                                    Institutional Portal <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </main>

            <Footer onLogin={onLogin} onNavigateToDocs={onNavigateToDocs} onNavigate={(p) => onNavigate(p as any)} />
        </div>
    );
};
